import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import {
  ArrowRight,
  Check,
  Clock,
  Code,
  FileJson,
  Lock,
  Activity,
  AlertTriangle,
  Zap,
  Shield,
} from 'lucide-react'

export const Route = createFileRoute('/_site/guides/api-monitoring')({
  component: ApiMonitoringGuidePage,
  head: () => ({
    meta: [
      {
        title:
          'API Uptime Monitoring: Catch Silent Failures — Uptrack',
      },
      {
        name: 'description',
        content:
          'HTTP 200 doesn\'t mean your API works. Validate JSON bodies with assertions and JSONPath, and catch silent failures with confirmed checks.',
      },
      {
        property: 'og:title',
        content: 'API Uptime Monitoring — Uptrack',
      },
      {
        property: 'og:description',
        content:
          'APIs fail silently. 200 OK doesn\'t mean correct. Monitor status codes, response bodies, and latency with Uptrack.',
      },
      {
        property: 'og:image',
        content: 'https://uptrack.app/og/guides-api-monitoring.png',
      },
    ],
    links: [
      {
        rel: 'canonical',
        href: 'https://uptrack.app/guides/api-monitoring',
      },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline:
            'API Uptime Monitoring: Catch Silent Failures Before Your Users Do',
          description:
            'How to monitor API endpoints beyond status codes — response body assertions, JSONPath validation, and consecutive-check confirmation to eliminate false alerts.',
          datePublished: '2026-05-22',
          dateModified: '2026-05-22',
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: { '@type': 'Organization', name: 'Uptrack', url: 'https://uptrack.app', logo: { '@type': 'ImageObject', url: 'https://uptrack.app/uptrack-logo-header.png' } },
        }),
      },
    ],
  }),
})

const competitors = [
  {
    name: 'Uptrack',
    bodyAssertions: 'JSONPath + Regex',
    minInterval: '30s',
    falseAlertPrevention: 'Consecutive checks',
    apiMonitorFree: '5 HTTP monitors',
    price: 'Free for 5, $3/mo for 50',
    highlight: true,
  },
  {
    name: 'Pingdom',
    bodyAssertions: 'String match only',
    minInterval: '60s',
    falseAlertPrevention: 'Single check',
    apiMonitorFree: 'No free tier',
    price: 'From $15/mo',
    highlight: false,
  },
  {
    name: 'Datadog',
    bodyAssertions: 'JSONPath + Regex',
    minInterval: '60s',
    falseAlertPrevention: 'Configurable retries',
    apiMonitorFree: '5 synthetic tests',
    price: 'From $23/mo',
    highlight: false,
  },
  {
    name: 'Better Stack',
    bodyAssertions: 'String match',
    minInterval: '30s',
    falseAlertPrevention: 'Confirmation period',
    apiMonitorFree: '5 monitors',
    price: 'From $20/mo',
    highlight: false,
  },
]

function ApiMonitoringGuidePage() {
  return (
    <>

      <>
        <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
          <h1 className="font-sans mb-4 text-4xl font-semibold">
            APIs fail silently. 200 OK doesn't mean correct.
          </h1>
          <p className="mb-8 text-lg leading-[1.7] text-muted-foreground">
            Your payment API returns 200 but the response body is{' '}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
              {`{"data": null}`}
            </code>
            . Your search endpoint responds in 50ms but every result is empty.
            Your auth service is up but issuing expired tokens. Traditional
            uptime monitoring sees green. Your customers see broken.
          </p>

          {/* What to monitor */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              What to actually monitor on an API
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              Pinging an endpoint and checking for 200 is table stakes. Real API
              uptime monitoring validates that the response is correct, fast, and
              secure.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-border bg-card p-5">
                <Activity className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">Status codes</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Assert the exact status code you expect. A 301 redirect when
                  you expect 200 means your routing is broken. A 204 instead of
                  200 means your API changed without your knowledge.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <Clock className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">Response time</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Set latency thresholds. An API that responds in 8 seconds is
                  functionally down for most users even if it returns the right
                  data.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <FileJson className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">
                  Response body (JSONPath assertions)
                </h3>
                <p className="text-lg leading-[1.7] text-body">
                  Validate specific fields in the JSON response. Check that{' '}
                  <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                    $.status
                  </code>{' '}
                  equals{' '}
                  <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                    "healthy"
                  </code>
                  , that{' '}
                  <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                    $.data.length
                  </code>{' '}
                  is greater than 0, or that a specific key exists.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <Lock className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">SSL certificates</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Get alerted days before your certificate expires. An expired
                  SSL cert on your API causes every client to reject the
                  connection instantly.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <Shield className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">Response headers</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Verify security headers like CORS, Content-Type, and
                  Cache-Control are present and correct. Missing CORS headers
                  break every frontend client silently.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <AlertTriangle className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">Error rate patterns</h3>
                <p className="text-lg leading-[1.7] text-body">
                  A single 500 might be a blip. Three consecutive 500s is an
                  outage. Track patterns over time to separate noise from real
                  incidents.
                </p>
              </div>
            </div>
          </section>

          {/* Code examples */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              DIY API health checks (and their limits)
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              You can build basic API monitoring with a cron job and a script.
              Here is what that looks like and where it falls short.
            </p>

            <div className="mb-6 rounded-lg border border-border bg-card p-4">
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                curl (quick health check)
              </p>
              <pre tabIndex={0} className="overflow-x-auto text-sm leading-relaxed">
                <code className="text-muted-foreground">{`# Check status code AND response body
curl -sf https://api.example.com/health \\
  -w "\\nHTTP_CODE:%{http_code} TIME:%{time_total}s\\n" \\
  | jq -e '.status == "healthy"' > /dev/null \\
  || echo "ALERT: API health check failed"`}</code>
              </pre>
            </div>

            <div className="mb-6 rounded-lg border border-border bg-card p-4">
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                Python (with response body validation)
              </p>
              <pre tabIndex={0} className="overflow-x-auto text-sm leading-relaxed">
                <code className="text-muted-foreground">{`import requests
import time

def check_api():
    start = time.time()
    try:
        r = requests.get(
            "https://api.example.com/health",
            headers={"Authorization": "Bearer YOUR_TOKEN"},
            timeout=10
        )
        elapsed = time.time() - start

        if r.status_code != 200:
            alert(f"Bad status: {r.status_code}")
            return

        data = r.json()
        if data.get("status") != "healthy":
            alert(f"Unhealthy: {data}")
            return

        if elapsed > 2.0:
            alert(f"Slow response: {elapsed:.1f}s")

    except requests.exceptions.Timeout:
        alert("API timed out after 10s")
    except Exception as e:
        alert(f"API unreachable: {e}")`}</code>
              </pre>
            </div>

            <div className="mb-6 rounded-lg border border-border bg-card p-4">
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                Node.js (fetch with assertions)
              </p>
              <pre tabIndex={0} className="overflow-x-auto text-sm leading-relaxed">
                <code className="text-muted-foreground">{`async function checkApi() {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10_000);

  try {
    const start = performance.now();
    const res = await fetch("https://api.example.com/health", {
      headers: { "Authorization": "Bearer YOUR_TOKEN" },
      signal: controller.signal,
    });
    const elapsed = performance.now() - start;

    if (!res.ok) {
      alert(\`Bad status: \${res.status}\`);
      return;
    }

    const data = await res.json();
    if (data.status !== "healthy") {
      alert(\`Unhealthy response: \${JSON.stringify(data)}\`);
      return;
    }

    if (elapsed > 2000) {
      alert(\`Slow: \${Math.round(elapsed)}ms\`);
    }
  } catch (err) {
    alert(\`API unreachable: \${err.message}\`);
  } finally {
    clearTimeout(timeout);
  }
}`}</code>
              </pre>
            </div>

            <div className="rounded-lg border border-status-degraded/30 bg-status-degraded/5 p-4">
              <p className="text-lg leading-[1.7] text-body">
                <span className="font-semibold text-status-degraded">
                  The problem with DIY:
                </span>{' '}
                You need to run these scripts from multiple regions, handle
                retries to avoid false alerts, store historical data for trend
                analysis, configure alert routing (email, Slack, PagerDuty), and
                keep the monitoring infrastructure itself reliable. That is a
                product, not a script.
              </p>
            </div>
          </section>

          {/* How Uptrack does it differently */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              How Uptrack monitors APIs differently
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              Most monitoring tools check if a URL returns 200. Uptrack treats
              each check as a full assertion pipeline: status code, latency,
              headers, and response body are all validated on every request.
            </p>

            <div className="space-y-6">
              <div className="flex gap-4">
                <Zap className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Consecutive-check confirmation (no false alerts)
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    A single failed check does not trigger an alert. Uptrack
                    runs a confirmation check from a different region before
                    creating an incident. This eliminates false positives from
                    transient network issues, DNS propagation delays, or
                    momentary load spikes. You only get alerted when something is
                    actually down.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <FileJson className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Response body assertions (JSONPath and regex)
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Define assertions on the response body using JSONPath
                    expressions or regex patterns. Check that{' '}
                    <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                      $.database.connected
                    </code>{' '}
                    is{' '}
                    <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                      true
                    </code>
                    , that an array has items, or that a version string matches a
                    pattern. If the body check fails, the endpoint is marked
                    down, even with a 200 status.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <Clock className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    30-second check intervals
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Detect API failures within seconds, not minutes. A 60-second
                    interval means you could be down for a full minute before
                    your first check even fires. With 30-second intervals, you
                    halve your detection time.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <Code className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Full HTTP method support
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Monitor GET, POST, PUT, PATCH, and DELETE endpoints. Send
                    custom request bodies, headers, and authentication tokens.
                    Test your actual API contract, not just whether the server
                    responds.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Competitor comparison */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              API monitoring tools compared
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              How Uptrack stacks up against other monitoring tools for API-specific
              features.
            </p>
            <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Scrollable table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">
                      Provider
                    </th>
                    <th className="pb-3 px-3 text-center font-medium text-muted-foreground">
                      Body assertions
                    </th>
                    <th className="pb-3 px-3 text-center font-medium text-muted-foreground">
                      Min interval
                    </th>
                    <th className="pb-3 px-3 text-center font-medium text-muted-foreground">
                      False alert prevention
                    </th>
                    <th className="pb-3 px-3 text-center font-medium text-muted-foreground">
                      Free tier
                    </th>
                    <th className="pb-3 pl-3 text-center font-medium text-muted-foreground">
                      Price
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {competitors.map((c) => (
                    <tr
                      key={c.name}
                      className={`border-b last:border-0 ${c.highlight ? 'bg-muted' : ''}`}
                    >
                      <td
                        className={`py-3 pr-4 font-medium ${c.highlight ? 'text-primary' : ''}`}
                      >
                        {c.name}
                      </td>
                      <td className="py-3 px-3 text-center text-muted-foreground">
                        {c.bodyAssertions}
                      </td>
                      <td className="py-3 px-3 text-center text-muted-foreground">
                        {c.minInterval}
                      </td>
                      <td className="py-3 px-3 text-center text-muted-foreground">
                        {c.falseAlertPrevention}
                      </td>
                      <td className="py-3 px-3 text-center text-muted-foreground">
                        {c.apiMonitorFree}
                      </td>
                      <td
                        className={`py-3 pl-3 text-center ${c.highlight ? 'font-medium text-primary' : 'text-muted-foreground'}`}
                      >
                        {c.price}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Setup guide */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              Monitor your first API endpoint in 3 steps
            </h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">
                  1
                </div>
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Enter your API endpoint
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Go to Dashboard, click Create Monitor, and paste your API
                    URL. Select the HTTP method (GET, POST, etc.) and add any
                    required headers like{' '}
                    <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                      Authorization: Bearer YOUR_TOKEN
                    </code>
                    . For POST requests, add a request body.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">
                  2
                </div>
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Add response assertions
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Set the expected status code (e.g., 200). Add body
                    assertions using JSONPath:{' '}
                    <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                      $.status == "ok"
                    </code>
                    . Set a response time threshold (e.g., alert if above 2000ms). Optionally validate specific response headers.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">
                  3
                </div>
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Configure alerts and go live
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Choose where to get notified: email, Slack, Discord, or
                    webhook. Set the check interval (as low as 30 seconds on Pro).
                    Save the monitor. Uptrack runs the first check immediately
                    and you will see results in your dashboard within seconds.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-border bg-card p-4">
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                Or create via API:
              </p>
              <pre tabIndex={0} className="overflow-x-auto text-sm leading-relaxed">
                <code className="text-muted-foreground">{`curl -X POST https://api.uptrack.app/api/monitors \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://api.example.com/health",
    "method": "GET",
    "interval": 30,
    "assertions": [
      {"type": "status_code", "value": 200},
      {"type": "json_path", "path": "$.status", "value": "healthy"},
      {"type": "response_time", "threshold": 2000}
    ]
  }'`}</code>
              </pre>
            </div>
          </section>

          {/* Use cases */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              API monitoring use cases
            </h2>
            <div className="space-y-4">
              {[
                {
                  title: 'REST APIs',
                  description:
                    'Monitor each critical endpoint independently. Your /users endpoint might be healthy while /payments is returning 500s. Per-endpoint monitoring catches partial outages that aggregate health checks miss.',
                },
                {
                  title: 'GraphQL APIs',
                  description:
                    'Send a POST request with a GraphQL query body and validate the response. Check that the "errors" array is empty and that expected fields are present in the response data.',
                },
                {
                  title: 'Webhooks',
                  description:
                    'Monitor the endpoints that receive webhooks from Stripe, GitHub, or Twilio. If your webhook handler goes down, events queue up and your integrations break silently.',
                },
                {
                  title: 'Third-party APIs',
                  description:
                    'Monitor the APIs you depend on: payment gateways, email services, SMS providers, CDNs. When Stripe or SendGrid has an issue, you will know before your customers report it.',
                },
                {
                  title: 'Microservices health checks',
                  description:
                    'Each microservice exposes a /health endpoint. Monitor them all from one dashboard. Correlate failures across services to identify which upstream dependency is causing cascading issues.',
                },
              ].map((uc) => (
                <div key={uc.title} className="flex gap-3">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />
                  <div>
                    <span className="font-semibold">{uc.title}</span>
                    <span className="text-muted-foreground">
                      {' '}
                      — {uc.description}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">FAQ</h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-sans mb-1 font-semibold">
                  Will monitoring requests trigger rate limiting on my API?
                </h3>
                <p className="text-lg leading-[1.7] text-body">
                  At 30-second intervals, Uptrack sends 2,880 requests per day
                  per monitor. Most APIs handle this easily. If your API has
                  strict rate limits, use a longer interval (60s or 120s) or
                  whitelist Uptrack's IP addresses in your rate limiter. You can
                  also monitor a dedicated health endpoint that is exempt from
                  rate limiting.
                </p>
              </div>
              <div>
                <h3 className="font-sans mb-1 font-semibold">
                  Can I send authentication headers?
                </h3>
                <p className="text-lg leading-[1.7] text-body">
                  Yes. Add any custom headers to your monitor configuration,
                  including{' '}
                  <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                    Authorization: Bearer
                  </code>{' '}
                  tokens, API keys, or custom auth headers. Headers are stored
                  encrypted and never exposed in logs or the UI after creation.
                </p>
              </div>
              <div>
                <h3 className="font-sans mb-1 font-semibold">
                  Can I monitor POST endpoints?
                </h3>
                <p className="text-lg leading-[1.7] text-body">
                  Yes. Uptrack supports GET, POST, PUT, PATCH, and DELETE
                  methods. For POST monitors, you can specify a JSON request body
                  that gets sent with every check. This is useful for monitoring
                  GraphQL APIs or endpoints that require a request payload.
                </p>
              </div>
              <div>
                <h3 className="font-sans mb-1 font-semibold">
                  Can I get alerted based on response time?
                </h3>
                <p className="text-lg leading-[1.7] text-body">
                  Yes. Set a response time threshold when creating a monitor. If
                  the API responds slower than your threshold on consecutive
                  checks, Uptrack creates a degraded performance incident. You
                  can set different thresholds per endpoint since a payment API
                  and a static health check have very different latency
                  expectations.
                </p>
              </div>
              <div>
                <h3 className="font-sans mb-1 font-semibold">
                  How do JSONPath assertions work?
                </h3>
                <p className="text-lg leading-[1.7] text-body">
                  JSONPath lets you target specific fields in a JSON response.
                  For example,{' '}
                  <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                    $.data.users.length
                  </code>{' '}
                  checks the number of users returned,{' '}
                  <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                    $.meta.version
                  </code>{' '}
                  checks a version string, and{' '}
                  <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                    $.services[0].status
                  </code>{' '}
                  checks the status of the first service in an array. If the
                  assertion fails, the check is marked as failed.
                </p>
              </div>
              <div>
                <h3 className="font-sans mb-1 font-semibold">
                  What happens if my API is behind a firewall?
                </h3>
                <p className="text-lg leading-[1.7] text-body">
                  You will need to whitelist Uptrack's monitoring IP addresses in
                  your firewall rules. Contact support for the current list of
                  IPs. Alternatively, expose a dedicated health check endpoint
                  that is accessible externally without compromising your
                  internal API security.
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <div className="rounded-lg border border-border bg-muted p-8 text-center">
            <Activity className="mx-auto mb-4 h-8 w-8 text-primary" />
            <h2 className="font-sans mb-3 text-xl font-semibold">
              Start monitoring your APIs
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              Pro includes 50 monitors at 30-second checks. Response body assertions, consecutive-check
              confirmation. Start free, or choose a plan for more.
            </p>
            <Button size="lg" asChild>
              <Link to="/pricing">
                See plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </article>
      </>

      </>
  )
}
